import React from 'react';
import Icon from '../../../components/AppIcon';

const MarketAnalysis = ({ analysisData }) => {
  const getSentimentColor = (sentiment) => {
    if (sentiment === 'Bullish') return 'text-success';
    if (sentiment === 'Bearish') return 'text-error';
    return 'text-warning';
  };

  const getSentimentIcon = (sentiment) => {
    if (sentiment === 'Bullish') return 'TrendingUp';
    if (sentiment === 'Bearish') return 'TrendingDown';
    return 'Minus';
  };

  const getStrengthColor = (strength) => {
    if (strength >= 80) return 'bg-success';
    if (strength >= 60) return 'bg-warning';
    if (strength >= 40) return 'bg-accent';
    return 'bg-error';
  };

  return (
    <div className="space-y-6">
      {/* Market Sentiment */}
      <div className="bg-card border border-border rounded-lg p-6 shadow-elevation-2">
        <div className="flex items-center space-x-3 mb-4">
          <Icon name="Activity" size={24} className="text-primary" />
          <h3 className="text-xl font-semibold text-card-foreground">Market Sentiment</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-muted rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-muted-foreground">Overall Sentiment</span>
              <Icon 
                name={getSentimentIcon(analysisData?.sentiment?.overall)} 
                size={20} 
                className={getSentimentColor(analysisData?.sentiment?.overall)} 
              />
            </div>
            <p className={`text-xl font-bold ${getSentimentColor(analysisData?.sentiment?.overall)}`}>
              {analysisData?.sentiment?.overall}
            </p>
            <div className="mt-2">
              <div className="w-full bg-border rounded-full h-2">
                <div 
                  className={`h-2 rounded-full ${getStrengthColor(analysisData?.sentiment?.strength)}`}
                  style={{ width: `${analysisData?.sentiment?.strength}%` }}
                ></div>
              </div>
              <p className="text-xs text-muted-foreground mt-1">Strength: {analysisData?.sentiment?.strength}%</p>
            </div>
          </div>

          <div className="bg-muted rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="MessageSquare" size={16} className="text-primary" />
              <span className="text-sm font-medium text-muted-foreground">Social Sentiment</span>
            </div>
            <p className={`text-xl font-bold ${getSentimentColor(analysisData?.sentiment?.social)}`}>
              {analysisData?.sentiment?.social}
            </p>
            <p className="text-sm text-muted-foreground">
              {analysisData?.sentiment?.socialScore}/100 Social Score
            </p>
          </div>

          <div className="bg-muted rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="Newspaper" size={16} className="text-primary" />
              <span className="text-sm font-medium text-muted-foreground">News Sentiment</span>
            </div>
            <p className={`text-xl font-bold ${getSentimentColor(analysisData?.sentiment?.news)}`}>
              {analysisData?.sentiment?.news}
            </p>
            <p className="text-sm text-muted-foreground">
              {analysisData?.sentiment?.newsScore}/100 News Score
            </p>
          </div>
        </div>
      </div>
      {/* Technical Analysis */}
      <div className="bg-card border border-border rounded-lg p-6 shadow-elevation-2">
        <div className="flex items-center space-x-3 mb-4">
          <Icon name="BarChart3" size={24} className="text-primary" />
          <h3 className="text-xl font-semibold text-card-foreground">Technical Analysis</h3>
        </div>

        <div className="space-y-4">
          {analysisData?.technicalIndicators?.map((indicator, index) => (
            <div key={index} className="flex items-center justify-between p-3 bg-muted rounded-lg">
              <div className="flex items-center space-x-3">
                <Icon name="TrendingUp" size={16} className="text-primary" />
                <div>
                  <p className="font-medium text-card-foreground">{indicator?.name}</p>
                  <p className="text-sm text-muted-foreground">{indicator?.description}</p>
                </div>
              </div>
              <div className="text-right">
                <p className={`font-bold ${getSentimentColor(indicator?.signal)}`}>
                  {indicator?.signal}
                </p>
                <p className="text-sm text-muted-foreground">{indicator?.value}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Key Factors */}
      <div className="bg-card border border-border rounded-lg p-6 shadow-elevation-2">
        <div className="flex items-center space-x-3 mb-4">
          <Icon name="Lightbulb" size={24} className="text-primary" />
          <h3 className="text-xl font-semibold text-card-foreground">Key Influencing Factors</h3>
        </div>

        <div className="space-y-3">
          {analysisData?.keyFactors?.map((factor, index) => (
            <div key={index} className="flex items-start space-x-3 p-3 bg-muted rounded-lg">
              <div className={`w-2 h-2 rounded-full mt-2 ${
                factor?.impact === 'Positive' ? 'bg-success' : 
                factor?.impact === 'Negative' ? 'bg-error' : 'bg-warning'
              }`}></div>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <p className="font-medium text-card-foreground">{factor?.title}</p>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    factor?.impact === 'Positive' ? 'bg-success/20 text-success' : 
                    factor?.impact === 'Negative' ? 'bg-error/20 text-error' : 'bg-warning/20 text-warning'
                  }`}>
                    {factor?.impact}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground">{factor?.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Correlation Analysis */}
      <div className="bg-card border border-border rounded-lg p-6 shadow-elevation-2">
        <div className="flex items-center space-x-3 mb-4">
          <Icon name="GitBranch" size={24} className="text-primary" />
          <h3 className="text-xl font-semibold text-card-foreground">Market Correlations</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {analysisData?.correlations?.map((correlation, index) => (
            <div key={index} className="flex items-center justify-between p-3 bg-muted rounded-lg">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                  <span className="text-white text-xs font-bold">
                    {correlation?.asset?.slice(0, 2)}
                  </span>
                </div>
                <div>
                  <p className="font-medium text-card-foreground">{correlation?.asset}</p>
                  <p className="text-sm text-muted-foreground">Correlation</p>
                </div>
              </div>
              <div className="text-right">
                <p className={`font-bold ${
                  correlation?.value > 0.7 ? 'text-success' : 
                  correlation?.value > 0.3 ? 'text-warning' : 'text-error'
                }`}>
                  {correlation?.value > 0 ? '+' : ''}{(correlation?.value * 100)?.toFixed(0)}%
                </p>
                <div className="w-16 bg-border rounded-full h-1 mt-1">
                  <div 
                    className={`h-1 rounded-full ${
                      correlation?.value > 0.7 ? 'bg-success' : 
                      correlation?.value > 0.3 ? 'bg-warning' : 'bg-error'
                    }`}
                    style={{ width: `${Math.abs(correlation?.value) * 100}%` }}
                  ></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MarketAnalysis;